import React from 'react';
import { Clock, LogOut, RefreshCw } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { Loader2 } from 'lucide-react';

interface DevicePendingPageProps {
    onRetry: () => void;
    isChecking?: boolean;
}

/**
 * DevicePendingPage: shown when a super admin's device fingerprint
 * is recognized but pending approval by another admin.
 */
const DevicePendingPage: React.FC<DevicePendingPageProps> = ({ onRetry, isChecking }) => {
    const { logout } = useAuth();

    return (
        <div className="flex items-center justify-center min-h-screen bg-[#FAFAFA]">
            <div className="w-full max-w-md flex flex-col items-center text-center space-y-6">
                {/* Icon */}
                <div className="w-16 h-16 rounded-2xl bg-amber-50 flex items-center justify-center">
                    <Clock className="w-8 h-8 text-amber-500" />
                </div>

                {/* Heading */}
                <div className="space-y-2">
                    <h2 className="text-xl font-semibold text-gray-900">
                        Awaiting Device Approval
                    </h2>
                    <p className="text-sm text-gray-500">
                        Your device has been registered and is waiting for another Super Admin to approve it.
                    </p>
                </div>

                {/* Info box */}
                <div className="w-full bg-gray-50 border border-gray-200 rounded-lg p-4 text-left">
                    <p className="text-xs font-semibold text-gray-700 mb-2">
                        What happens next?
                    </p>
                    <ul className="text-xs text-gray-500 space-y-1.5">
                        <li>• Another Super Admin needs to approve your device</li>
                        <li>• Once approved, refresh this page to continue</li>
                        <li>• Contact your team if you need immediate access</li>
                    </ul>
                </div>

                {/* Check Status button */}
                <button
                    onClick={onRetry}
                    disabled={isChecking}
                    className="w-full py-2.5 px-4 bg-gray-900 text-white text-sm font-medium rounded-lg
                        hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed
                        transition-colors flex items-center justify-center gap-2"
                >
                    {isChecking ? (
                        <>
                            <Loader2 className="w-4 h-4 animate-spin text-white" />
                            Checking...
                        </>
                    ) : (
                        <>
                            <RefreshCw className="w-4 h-4 text-white" />
                            Check Approval Status
                        </>
                    )}
                </button>

                {/* Sign out link */}
                <button
                    onClick={logout}
                    className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900 transition-colors"
                >
                    <LogOut className="w-3.5 h-3.5" />
                    Sign Out
                </button>
            </div>
        </div>
    );
};

export default DevicePendingPage;
